import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Header from "../Header";
import Footer from "../Footer";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import config from "../common/Config";
import { setTableWidths, getTableWidths } from "../../data/postAPi";
import { format } from "date-fns";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  TextField,
  Chip,
  Link as MuiLink,
  Alert,
  Divider,
  Stack,
  InputAdornment,
  Button,
  Grid2,
} from "@mui/material";
import { Iconify } from "src/index";
import "../../components/css/custom-filter.css";
import "../common/commonStyles.css";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const columns = [
  { id: "date", label: "Order Date", numeric: false, width: 120 },
  { id: "reference_no", label: "Order Id", numeric: false, width: 120 },
  { id: "customer_with_account", label: "Customers", numeric: false, width: 150 },
  { id: "grand_total", label: "Order Amount", numeric: true, width: 130 },
  { id: "sale_person", label: "Sales Person Name", numeric: false, width: 150 },
  { id: "sale_status", label: "Status", numeric: false, width: 100 },
  { id: "reject_reason", label: "Reason", numeric: false, width: 180 },
];

const statusStyles = {
  Rejected: { backgroundColor: "#facc15", color: "#1f2937" },
  Intransit: { backgroundColor: "#15803d", color: "#ffffff" },
  Undelivered: { backgroundColor: "#dc2626", color: "#ffffff" },
};

const defaultColWidths = [120, 120, 150, 130, 150, 100, 180];

const RejectSales = () => {
  const [dates, setDates] = useState([null, null]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [rejectedTotal, setRejectedTotal] = useState("");
  const [currentRejectedTotal, setCurrentRejectedTotal] = useState("");
  const accessToken = typeof window !== "undefined" ? localStorage.getItem("accessToken") : null;
  const userId = typeof window !== "undefined" ? localStorage.getItem("userId") : null;
  const tableName = "rejectSalesTable";
  const [colWidths, setColWidths] = useState(defaultColWidths);
  const refreshRef = useRef(0);
  const token = localStorage.getItem("token");

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(null, args), wait);
    };
  };

  const debouncedSetTableWidths = useRef(
    debounce((uid, name, widths) => {
      if (uid) {
        setTableWidths(uid, name, widths);
      }
    }, 500)
  ).current;

  const startResizing = (index, event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = colWidths[index];
    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(70, startWidth + moveEvent.clientX - startX);
      setColWidths((widths) => {
        const updated = [...widths];
        updated[index] = newWidth;
        debouncedSetTableWidths(userId, tableName, updated);
        return updated;
      });
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    let isMounted = true;
    getTableWidths(userId, tableName)
      .then((res) => {
        const widths = res?.data?.widths;
        if (isMounted && Array.isArray(widths) && widths.length === columns.length) {
          setColWidths(widths);
        }
      })
      .catch(() => {
        setColWidths(defaultColWidths);
      });
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    setLoading(true);
    setError("");
    (async () => {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      let reqConfig = {
        headers: {
          authorization: `Bearer ${token}`,
        },
      };
      let payload;
      if (useEncrypt) {
        const plain = {
          draw: String(refreshRef.current + 1),
          start: String(page * rowsPerPage),
          length: String(rowsPerPage),
          search: { value: searchTerm, regex: "false" },
          start_date: startDate,
          end_date: endDate,
          order: [{ column: "0", dir: "desc" }],
          columns: columns.map((column) => ({
            data: column.id,
            name: "",
            searchable: "false",
            orderable: "false",
            search: { value: "", regex: "false" },
          })),
        };
        const ep = encryptPayload(plain);
        if (!ep) {
          setLoading(false);
          setError("Unable to load rejected sales.");
          return;
        }
        const fd = new FormData();
        fd.append("encrypted_payload", ep);
        payload = fd;
      } else {
        const params = new URLSearchParams();
        params.append("draw", String(refreshRef.current + 1));
        params.append("start", String(page * rowsPerPage));
        params.append("length", String(rowsPerPage));
        params.append("search[value]", searchTerm);
        params.append("search[regex]", "false");
        params.append("start_date", startDate);
        params.append("end_date", endDate);
        params.append("order[0][column]", "0");
        params.append("order[0][dir]", "desc");
        columns.forEach((column, index) => {
          params.append(`columns[${index}][data]`, column.id);
          params.append(`columns[${index}][name]`, "");
          params.append(`columns[${index}][searchable]`, "false");
          params.append(`columns[${index}][orderable]`, "false");
          params.append(`columns[${index}][search][value]`, "");
          params.append(`columns[${index}][search][regex]`, "false");
        });
        payload = params;
        reqConfig.headers["Content-Type"] = "application/x-www-form-urlencoded";
      }
      axios
        .post(`${config.API_URL}/get_reject_sales`, payload, reqConfig)
        .then((response) => {
          const payload = normalizeEncryptedApiResponse(response.data, useEncrypt) || response.data || {};
          const rows = Array.isArray(payload.data) ? payload.data : [];
          setData(rows);
          const filteredCount = Number(payload.recordsFiltered);
          const totalCount = Number.isFinite(filteredCount) ? filteredCount : Number(payload.recordsTotal) || rows.length;
          setTotalRows(totalCount);
          setRejectedTotal(payload.total_rejected_total || "");
          setCurrentRejectedTotal(payload.total_rejected_total1 || "");
          refreshRef.current += 1;
        })
        .catch((err) => {
          const message = err.response?.data?.message || err.message || "Unable to load rejected sales.";
          setData([]);
          setTotalRows(0);
          setRejectedTotal("");
          setCurrentRejectedTotal("");
          setError(message);
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [accessToken, page, rowsPerPage, searchTerm, startDate, endDate]);

  const handleDateChange = (value) => {
    const range = Array.isArray(value) ? value : [null, null];
    setDates(range);
    const start = range[0] ? format(range[0], "yyyy-MM-dd") : "";
    const end = range[1] ? format(range[1], "yyyy-MM-dd") : "";
    setStartDate(start);
    setEndDate(end);
    setPage(0);
  };

  const handleSubmit = () => {
    setSearchTerm(searchInput.trim());
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilter = () => {
    setDates([null, null]);
    setStartDate("");
    setEndDate("");
    setSearchInput("");
    setSearchTerm("");
    setPage(0);
  };

  const getStatusStyle = (status) => statusStyles[status] || { backgroundColor: "#6b7280", color: "#ffffff" };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} alignItems="center" justifyContent="space-between">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Box>
                  <Typography className="page-title">
                    Reject Sales
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  size="small"
                  placeholder="Search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmit();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill"/>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }} sx={{
                display: "flex",
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: { xs: 'center', md: 'flex-end' },
                alignItems: "flex-end",
                gap: 1,
              }}>

                <Box sx={{ width: { xs: '100%', md: '350px' } }}>
                  <CommonDatePicker className="input-border" value={dates} onChange={handleDateChange} format="dd/MM/yyyy" character=" - " style={{ width: "100%" }} />
                </Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  className="button-variant-outlined"
                  startIcon={<Iconify icon="eva:refresh-outline" width={18} height={18} />}
                  onClick={handleClearFilter}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  Clear
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table
              size="medium"
              className="table-cell-border"
              sx={{
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow
                  className="table-cell-header"
                >
                  {columns.map((column, index) => (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? "right" : "left"}
                      style={{ width: colWidths[index], minWidth: 70, position: "relative" }}
                      sx={{
                        width: column.width ? `${column.width}px` : colWidths[index] ? `${colWidths[index]}px` : "auto",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={column.numeric ? "flex-end" : "flex-start"}
                      >
                        {column.label}
                        {index < colWidths.length - 1 && (
                          <Box className="th-resizer" onMouseDown={(event) => startResizing(index, event)} />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                      <Typography align="center">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow
                      key={row.id || row.reference_no}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.04)"
                        },
                      }}
                    >
                      {columns.map((column, colIndex) => {
                        const cellValue = column.id === "reference_no" ? (
                          <MuiLink
                            href={`${config.BASE_URL}/order_summary/print/${row.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "inherit",
                              textDecoration: "underline !important",
                              textDecorationColor: "currentColor",
                              textUnderlineOffset: "2px",
                              "&:hover": {
                                textDecoration: "underline !important",
                                textDecorationColor: "currentColor"
                              }
                            }}
                          >
                            {row.reference_no}
                          </MuiLink>
                        ) : column.id === "sale_status" ? (
                          <Chip
                            label={row.sale_status || "-"}
                            size="small"
                            variant="soft"
                            sx={{
                              fontSize: "0.75rem",
                            }}
                          />
                        ) : column.id === "reject_reason" ? (
                          row.reject_reason || row.reason_note || row.hold_reason || row.reason || "-"
                        ) : (
                          row[column.id] || "-"
                        );

                        return (
                          <TableCell
                            key={column.id}
                            align={column.numeric ? "right" : "left"}
                            className="table-cell"
                            sx={{
                              width: column.width ? `${column.width}px` : "auto",
                              ...(column.numeric && { fontWeight: 500 })
                            }}
                          >
                            {cellValue}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default RejectSales;